import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "maintenance-systems",
  kind: "horizontal",
  title: "Maintenance Workflow Systems",
  shortTitle: "Maintenance Systems",
  status: "draft",
  tier: "experience",
  seoTitle: "Maintenance Workflow Systems | General Applications",
  seoDescription:
    "Maintenance planning, execution, status, exception, parts, and sustainment systems connected to authoritative technical data.",
  summary:
    "Maintenance systems that connect planning, technical data, configuration, parts, execution, exceptions, and sustainment into a record of work people can act on and trust.",
  image: "/images/blog/s1000d-conversion-failure-modes-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["military", "energy"],
  relatedPostSlugs: [
    "operational-task-management",
    "s1000d-issue-archaeology",
    "scrubbing-the-edge",
    "smart-forms-for-harsh-environments",
  ],
  engineering: [
    "Maintenance planning, work-package, execution, status, and sustainment workflows",
    "Technical-data, procedure, part, configuration, and task relationships",
    "Structured work records, readings, acknowledgments, attachments, and sign-off",
    "Exception, deferment, correction, escalation, and return-to-service state models",
    "Parts, materials, requests, and maintenance-information integration",
    "Operator, maintainer, planner, and reviewer interfaces built around real handoffs",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Maintenance Is a Connected System",
    },
    {
      type: "paragraph",
      text: "Maintenance work is not a ticket moving through a queue. It connects an asset and its current configuration to technical data, procedures, parts, readings, people, approvals, exceptions, and the next operational decision. A system that separates those facts makes the technician reconstruct the context at the moment it matters most.",
    },
    {
      type: "heading",
      level: 2,
      text: "Plan for the Work That Changes",
    },
    {
      type: "paragraph",
      text: "The normal procedure is only part of the design. A maintenance system needs to show what is pending, in progress, deferred, blocked, corrected, approved, and ready to return to service. Those states must carry the reason, authority, technical context, and evidence needed for the next person to make a sound decision.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map the asset, maintenance work, technical information, parts, roles, exceptions, and handoffs that shape how work is actually planned and completed.",
      produces: [
        "Maintenance lifecycle and work-state model",
        "Asset, configuration, procedure, parts, and record relationship map",
        "Role, authority, handoff, and escalation analysis",
        "Current-system, data-quality, and exception-path assessment",
      ],
      fit: {
        good: [
          "Organizations where maintenance work spans multiple systems, handoffs, technical sources, and approval points",
          "Teams replacing disconnected tickets, paper records, spreadsheets, and technical references with a coherent operational workflow",
        ],
        poor: [
          "A simple work-order tracker with no technical context, exception model, or sustainment requirement",
          "A request to automate maintenance decisions while leaving technical authority and return-to-service responsibility undefined",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the maintenance state model and the relationships that bring asset, configuration, technical data, parts, work, and authority together.",
      produces: [
        "Maintenance planning, execution, and sustainment architecture",
        "Work package, task, status, exception, and return-to-service model",
        "Asset, configuration, procedure, part, and evidence relationship design",
        "Role, approval, notification, and escalation model",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the records, interfaces, validation, integrations, and exception paths that support maintenance work under real conditions.",
      produces: [
        "Maintenance work-package and execution interface specifications",
        "Technical-data, parts, configuration, and record integration contracts",
        "Exception, deferment, correction, approval, and release procedures",
        "Status, handoff, reporting, and sustainment definitions",
      ],
    },
    implementation: {
      focus:
        "Build and validate the system with planners, maintainers, and reviewers against both ordinary work and the exceptions that test the model.",
      produces: [
        "Connected maintenance planning and execution workflows",
        "Structured work, status, exception, and evidence records",
        "Traceable relationships among maintenance, technical data, parts, and configuration",
        "Operational adoption and continuous-improvement guidance",
      ],
    },
  },
};
